import "./lavori.scss";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLavoroDataSource } from "../../datasources/lavoroDataSource";
import { confirm } from "devextreme/ui/dialog";
import { Button, DataGrid, DateBox, LoadPanel, SelectBox, SpeedDialAction, Toast } from "devextreme-react";
import { Column, Toolbar, DataGridRef, Sorting, Selection, DataGridTypes, Summary, TotalItem } from "devextreme-react/data-grid";
import { Item } from "devextreme-react/toolbar";
import { LavoriApi, PersonaleApi } from "../../data";
import useApiConfiguration from "../../apiConfig";
import { useUserSessionContext } from "../../contexts/UserSessionContext";
import { useScreenSize, useScreenSizeClass } from "../../utils/media-query";
import { LavoriPopupEdit, LavoriPopupAttivitaMensile, LavoriPopupRiepilogo, LavoriPopupSpese, LavoriPopupCopia } from "../../components";
import { PopupEditHandle } from "../../components/popup-lavori/popupEdit";
import { PopupRiepilogoHandle } from "../../components/popup-lavori/popupRiepilogo";
import { PopupAttivitaMensileHandle } from "../../components/popup-lavori/popupAttivitaMensile";
import { PopupSpeseHandle } from "../../components/popup-lavori/popupSpese";
import { PopupCopiaLavoroHandle } from "../../components/popup-lavori/popupCopiaLavoro";
import config from "devextreme/core/config";

export default function Lavori() {
    const gridRef = useRef<DataGridRef>(null);
    const popupEditRef = useRef<PopupEditHandle>(null);
    const popupRiepilogoRef = useRef<PopupRiepilogoHandle>(null);
    const popupAttivitaMensileRef = useRef<PopupAttivitaMensileHandle>(null);
    const popupSpeseRef = useRef<PopupSpeseHandle>(null);
    const popupCopiaRef = useRef<PopupCopiaLavoroHandle>(null);
    const apiConfiguration = useApiConfiguration();
    const {user} = useUserSessionContext();
    const {isLarge, isMedium} = useScreenSize();
    const screenSizeClass = useScreenSizeClass();

    const [loading, setLoading] = useState(false);
    const [data, setDate] = useState(new Date());
    const [personaleId, setPersonaleId] = useState(user.id);
    const [dataSourcePersonale, setDataSourcePersonale] = useState<any[]>([]);
    const [canChangePersonale, setChangePersonale] = useState(false);
    const [selectedIdLavoro, setSelectedIdLavoro] = useState(-1);
    const [selectedCommessa, setSelectedCommessa] = useState('');
    const [commessaCanEdit, setCommessaCanEdit] = useState<boolean>(false);

    const [toastConfig, setToastConfig] = useState({ isVisible: false, message: '' } as { isVisible: boolean, message: string });

    const [enableAdd, setEnableAdd] = useState(false);
    const [enableEdit, setEnableEdit] = useState(false);

    config({
        floatingActionButtonConfig: {
            icon: "optionsgear",
            shading: true,
            position: {
                my: "right bottom",
                at: "right bottom",
                of: "#gridLavori",
                offset: '-16 -16'
            }
        }
    });

    function handleGridRowClick(e: DataGridTypes.RowClickEvent) {
        e.event.preventDefault();
        e.event.stopPropagation();

        const idLavoro = e.data?.idLavoro;

        if (idLavoro == null) return;

        if (idLavoro == selectedIdLavoro) {
            setSelectedIdLavoro(-1);
            setSelectedCommessa('');
            setCommessaCanEdit(false);
            gridRef.current.instance().deselectAll();
        } else {
            setSelectedIdLavoro(idLavoro);
            setSelectedCommessa(e.data?.commessa);
            setCommessaCanEdit(e.data?.allowEdit);
        }
    };

    function changeDipendente(e: any) {
        setPersonaleId(e.value);
    };

    function changeDay(offset: number) {
        const newDate = new Date(data);
        newDate.setDate(newDate.getDate() + offset);
        setDate(newDate);
    };

    function setGiornataSmartworking() {
        setLoading(true);
        var api = new LavoriApi(apiConfiguration);

        api.setGiornataSmartworking({
            data: data,
            idPersonale: personaleId
        })
        .then(() => {
            setToastConfig({
                ...toastConfig,
                isVisible: true,
                message: 'Tutte le commesse sono state segnate come smartworking'
            });
        })
        .finally(() => {
            setSelectedIdLavoro(-1);
            setSelectedCommessa('');
            setCommessaCanEdit(false);
            gridRef.current.instance().deselectAll();

            setLoading(false);
        });
    };

    function copiaGiornoPrecedente() { 
        setLoading(true);
        var api = new LavoriApi(apiConfiguration);

        api.copiaGiornoPrecedente({
            data: data,
            idPersonale: personaleId
        })
        .then(() => {
            refresh();

            setToastConfig({
                ...toastConfig,
                isVisible: true,
                message: 'Giorno precedente copiato correttamente'
            });
        })
        .finally(() => setLoading(false));
    };

    function openCopiaLavoro(idLavoro: number) {
        popupCopiaRef.current?.show(idLavoro);
    };

    function lavoroCopiato()  {
        setToastConfig({
            ...toastConfig,
            isVisible: true,
            message: 'Lavoro copiato correttamente'
        });
    };

    function checkDelete(id: number) {
        const result = confirm("Confermi l'eliminazione?", "Conferma");
        result.then((dialogRes) => {
            if (dialogRes) 
                deleteLavoro(id);
        });
    };

    function openSpese(idLavoro: number, commessa: string) {
        popupSpeseRef.current?.show(idLavoro, commessa);
    }

    function newLavoro(e: any) {
        popupEditRef.current?.new(personaleId, data);
    };

    function updateLavoro(idLavoro: number) {
        popupEditRef.current?.edit(idLavoro, personaleId, data);
    };

    function deleteLavoro(idLavoro: number) {
        var api = new LavoriApi(apiConfiguration);

        api.deleteLavoro({ id: idLavoro })
        .then(() => {
            refresh();
            setToastConfig({
                ...toastConfig,
                isVisible: true,
                message: 'Cancellazione completata'
            });
        });
    };

    function openRiepilogo() {
        popupRiepilogoRef.current?.show(personaleId, data);
    };

    function openRimborsi() {
        setLoading(true);

        var api = new LavoriApi(apiConfiguration);
        api.getReportSpeseMensili({ idPersonale: personaleId, data: data })
        .then((res) => {
            openPDF(res.base64);
        })
        .finally(() => setLoading(false));
    };

    function openPDF(base64: string) {
        const byteChars = atob(base64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
            byteNumbers[i] = byteChars.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
    };

    function openAttivitaMensile() {
        popupAttivitaMensileRef.current?.show(personaleId, data);
    };

    const refresh = useCallback(() => {
        gridRef.current?.instance().refresh();
    }, []);

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false
        });
    }, [toastConfig, setToastConfig]);
    
    useEffect(() => {
        const api = new PersonaleApi(apiConfiguration);
        api.getPersonale({ date: data }).then((res) => {
            setDataSourcePersonale(res);
            setChangePersonale(res.length > 1);
        });
    }, [data]);

    useEffect(() => {
        const api = new LavoriApi(apiConfiguration);
        api.getBloccoCaricamentoLavori({ data, idPersonale: personaleId }).then((res) => {
            setEnableAdd(res.allowNew);
            setEnableEdit(res.allowEdit);
        });
    }, [data, personaleId]);

    useEffect(() => {
        gridRef.current.instance().deselectAll();
        setSelectedIdLavoro(-1);
    }, [isLarge, isMedium]);

    return (
        <React.Fragment>
            {
                loading && <LoadPanel style={{ zIndex: 9999 }} shading={true} shadingColor="rgba(0, 0, 0, 0.4)" position={'center'} visible={true} />
            }

            <div className="view-list-lavori">
                <div className={`view-wrapper view-wrapper-list list-page ${screenSizeClass}`}>
                    <DataGrid
                        id="gridLavori" ref={gridRef} key="idLavoro"
                        dataSource={useLavoroDataSource(personaleId, data, data)}
                        showBorders={true} rowAlternationEnabled={true}
                        defaultFocusedRowIndex={0} noDataText="Nessun lavoro caricato"
                        className="grid theme-dependent" onRowClick={handleGridRowClick}
                    >
                        <Sorting mode="single" />

                        {
                            !isLarge && !isMedium && (
                                <Selection mode="single" />
                            )
                        }
                        
                        <Toolbar>
                            <Item location="before">
                                <div className="grid-header">Lavori</div>
                            </Item>

                            <Item
                                location="center"
                                locateInMenu="auto"
                                showText="inMenu"
                                widget="dxSelectBox"
                                visible={canChangePersonale}
                            >
                                <SelectBox className="dx-dipendente"
                                    label="Dipendente" labelMode="floating"
                                    dataSource={dataSourcePersonale}
                                    displayExpr="descrizione"
                                    valueExpr="idPersonale"
                                    searchEnabled={true}
                                    value={personaleId}
                                    onValueChanged={changeDipendente}
                                />
                            </Item>

                            <Item
                                location="center"
                                locateInMenu="never"
                                showText="inMenu"
                                widget="dxDateBox"
                            >
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    {
                                        isLarge && (
                                            <Button
                                                icon="chevronleft"
                                                stylingMode="text"
                                                onClick={() => changeDay(-1)}
                                            />
                                        )
                                    }

                                    <DateBox
                                        className={`dataSearch ${screenSizeClass}`}
                                        label="Data" labelMode="floating"
                                        type="date" value={data}
                                        onValueChanged={(e) => setDate(e.value ?? new Date())}
                                        displayFormat={ isLarge ? "EEEE d MMMM yyyy" : "dd/MM/yyyy"}
                                        inputAttr={{ readonly: (isLarge || isMedium) }}
                                        showClearButton={false} openOnFieldClick
                                    />
                                    
                                    {
                                        isLarge && (
                                            <Button
                                                icon="chevronright"
                                                stylingMode="text"
                                                onClick={() => changeDay(1)}
                                            />
                                        )
                                    }
                                </div>
                            </Item>

                            <Item
                                location="after"
                                locateInMenu="auto"
                                showText="inMenu"
                                widget="dxButton"
                            >
                                <Button 
                                    icon="add" text="Nuovo lavoro" disabled={!enableAdd} 
                                    hint="Aggiungi lavoro" visible={isLarge || isMedium}
                                    stylingMode="text" onClick={newLavoro}
                                />
                            </Item>

                            <Item 
                                location='after'
                                locateInMenu="auto"
                                showText="inMenu"
                                widget="dxButton"
                            >
                                <Button  
                                    icon="copy" text="Copia giorno precedente" disabled={!enableAdd}
                                    hint="Copia giorno precedente" visible={isLarge || isMedium}
                                    stylingMode="text" onClick={copiaGiornoPrecedente}
                                />
                            </Item>

                            <Item
                                location="after"
                                locateInMenu="auto"
                                showText="inMenu"
                                widget="dxButton"
                            >
                                <Button text="Smartworking" disabled={!enableAdd} hint="Giornata smartworking" visible={isLarge || isMedium} stylingMode="text" onClick={setGiornataSmartworking}>
                                    <i className='dx-icon material-symbols-outlined'>home_work</i>
                                </Button>
                            </Item>

                            <Item
                                location="after"
                                locateInMenu="auto"
                                showText="inMenu"
                                widget="dxButton"
                            >
                                <Button
                                    icon="refresh"
                                    text="Refresh"
                                    stylingMode="text"
                                    onClick={refresh}
                                />
                            </Item>

                            <Item
                                location="after"
                                locateInMenu="always"
                                showText="inMenu"
                                widget="dxButton"
                            >
                                <Button 
                                    icon="checklist"
                                    text="Resoconto mensile"
                                    stylingMode="text"
                                    onClick={openRiepilogo}
                                />
                            </Item>

                            <Item
                                location="after"
                                locateInMenu="always"
                                showText="inMenu"
                                widget="dxButton"
                            >
                                <Button 
                                    icon="print"
                                    text="Attività mensile"
                                    stylingMode="text"
                                    onClick={openAttivitaMensile}
                                />
                            </Item>

                            <Item
                                location="after"
                                locateInMenu="always"
                                showText="inMenu"
                                widget="dxButton"
                            >
                                <Button 
                                    icon="print"
                                    text="Rimborsi mensili"
                                    stylingMode="text"
                                    onClick={openRimborsi}
                                />
                            </Item>
                        </Toolbar>

                        <Column
                            visible={enableEdit && (isLarge || isMedium)}
                            width={150}
                            cellRender={(cell) => (
                                <div style={{ display: "flex" }}>
                                    <Button icon="money" hint="Spese" stylingMode="text" visible={cell.data.allowEdit} onClick={() => openSpese(cell.data.idLavoro, cell.data.commessa) } />
                                    <Button icon="copy" hint="Copia" stylingMode="text" visible={cell.data.allowEdit} onClick={() => openCopiaLavoro(cell.data.idLavoro) } />
                                    <Button icon="edit" hint="Modifica" stylingMode="text" visible={cell.data.allowEdit} onClick={() => updateLavoro(cell.data.idLavoro) } />
                                    <Button icon="trash" hint="Elimina" stylingMode="text" visible={cell.data.allowEdit} onClick={() => checkDelete(cell.data.idLavoro) } />
                                </div>
                            )}
                        />
                        <Column dataField="idLavoro" caption="" visible={false} />
                        <Column dataField="commessa" caption="Commessa" />
                        <Column dataField="note" caption="Note" hidingPriority={0} />
                        <Column
                            dataField="oreLavorate"
                            alignment="center"
                            width={90}
                            caption="Ore"
                        />

                        <Summary>
                            <TotalItem 
                                column='oreLavorate'
                                summaryType='sum'
                                displayFormat="Ore totali: {0}"
                                valueFormat={{ type: "decimal", precision: 1 }}
                                showInColumn="commessa"
                            />
                        </Summary>
                    </DataGrid>

                    <SpeedDialAction 
                        icon="add" label="Carica lavoro"
                        visible={enableAdd && !isLarge && !isMedium && (selectedIdLavoro < 0)}
                        index={1} onClick={newLavoro}
                    />
                    <SpeedDialAction 
                        icon="copy" label="Copia giorno precedente"
                        visible={enableAdd && !isLarge && !isMedium && (selectedIdLavoro < 0)}
                        index={2} onClick={copiaGiornoPrecedente}
                    />
                    <SpeedDialAction 
                        icon="custom-homework material-symbols-outlined" label="Giornata smartworking"
                        visible={enableEdit && !isLarge && !isMedium && (selectedIdLavoro < 0)}
                        index={3} onClick={setGiornataSmartworking}
                    />
                    <SpeedDialAction 
                        icon="trash" label="Elimina"
                        visible={enableEdit && commessaCanEdit && !isLarge && !isMedium && (selectedIdLavoro > 0)}
                        index={1} onClick={() => checkDelete(selectedIdLavoro)}
                    />
                    <SpeedDialAction 
                        icon="edit" label="Modifica"
                        visible={enableEdit && commessaCanEdit && !isLarge && !isMedium && (selectedIdLavoro > 0)}
                        index={2} onClick={() => updateLavoro(selectedIdLavoro)}
                    />
                    <SpeedDialAction 
                        icon="copy" label="Copia"
                        visible={enableEdit && commessaCanEdit && !isLarge && !isMedium && (selectedIdLavoro > 0)}
                        index={3} onClick={() => openCopiaLavoro(selectedIdLavoro)}
                    />
                    <SpeedDialAction 
                        icon="money" label="Spese"
                        visible={enableEdit && commessaCanEdit && !isLarge && !isMedium && (selectedIdLavoro > 0)}
                        index={4} onClick={() => openSpese(selectedIdLavoro, selectedCommessa)}
                    />
                </div>
            </div>

            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                height={45} width={200}
                position={{ at: 'right bottom', my: 'right bottom', of: window, offset: { x: -20, y: -20 } }}
                type="success"
                onHiding={onHidingToast} closeOnClick = {true}
                displayTime={3000}
                animation={{ show: { type: 'fade', duration: 400, from: 0, to: 1}, hide: { type: 'fade', duration: 40, to: 0 } }}
            />

            <LavoriPopupEdit ref={popupEditRef} onChange={refresh} />
            <LavoriPopupRiepilogo ref={popupRiepilogoRef} />
            <LavoriPopupAttivitaMensile ref={popupAttivitaMensileRef} />
            <LavoriPopupSpese ref={popupSpeseRef} onChange={refresh} />
            <LavoriPopupCopia ref={popupCopiaRef} onChange={lavoroCopiato} />
        </React.Fragment>
    );
}